<template>
  <div class="border-b border-[#E0E0E0] border-opacity-90 border-solid flex items-center justify-between px-6 py-5">
    <div class="">
      <div class="text-[#414141] text-[18px] leading-[1.75rem] mb-1 font-semibold">
        Module {{ moduleNumber }}
      </div>
      <h1 class="font-minion text-[#00423D] text-[1.5rem] leading-[1.75rem] font-semibold">{{ title }}</h1>
    </div>
    <div class="flex flex-col items-end">
      <AssigneeSelector />
      <div v-if="route.path.includes('/steps/funding-new-projects/overview')" class="mt-[0.5rem]">
        <p class="flex flex-wrap justify-end text-[#00423D] text-[14px] leading-[22px]">
          <template v-if="isModuleSkipped">
            <div class="flex items-center justify-center">
              This Module is Skipped
              <a @click.prevent="handleUnskip" data-action="unskip" class="flex items-center underline ml-2 text-[#00423D] font-semibold text-[18px] leading-[22px] cursor-pointer">
                Unskip it 
                <img class="ml-1 w-[16px] h-[16px] align-middle" src="@/assets/arrow-right-clr.svg" alt="" />
              </a>
            </div>
          </template>
          <template v-else>
            <div class="flex items-center justify-center">
              This Module is Optional 
              <a @click.prevent="handleSkip" data-action="skip" class="flex items-center underline ml-2 text-[#00423D] font-semibold text-[18px] leading-[22px] cursor-pointer">
                Skip it 
                <img class="ml-1 w-[16px] h-[16px] align-middle" src="@/assets/arrow-right-clr.svg" alt="" />
              </a>
            </div>
          </template>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import AssigneeSelector from "./AssigneeSelector.vue";
import { useSkippedModulesStore } from '@/stores/skippedModules';

const router = useRouter();
const route = useRoute();
const skippedModulesStore = useSkippedModulesStore();

const props = defineProps({
  moduleNumber: {
    type: Number,
    required: true
  },
  title: {
    type: String,
    required: true
  }
});

const isModuleSkipped = computed(() => {
  // Check if the skipped modules store is initialized
  if (!skippedModulesStore.initialized || !Array.isArray(skippedModulesStore.skippedRoutes)) {
    return false;
  }
  
  // Extract the current module path from the route (just the module path, not including section)
  const pathParts = route.path.split('/');
  if (pathParts.length < 3) {
    return false;
  }
  
  // Get just the module path (first two parts after the leading slash)
  const modulePath = `/${pathParts[1]}/${pathParts[2]}`;
  
  // For module 9, always show as skipped if it's in the skipped routes
  if (modulePath === '/steps/funding-new-projects') {
    return skippedModulesStore.skippedRoutes.includes(modulePath);
  }
  
  // Check if this module path is in the skipped routes array
  return skippedModulesStore.skippedRoutes.includes(modulePath);
});

const handleSkip = async () => {
  try {    
    // Get the current module path from the route
    const currentPath = route.path;
    const pathParts = currentPath.split('/');
    
    if (pathParts.length < 3) {
      console.error('Invalid route path format for skipping');
      return;
    }
    
    // Extract the module path (only the first 3 parts of the path)
    const currentModulePath = `/${pathParts[1]}/${pathParts[2]}`;
    
    // Initialize the routes to skip array with the current module
    let routesToSkip = [currentModulePath];
    

    
    // Clean up any previous unskip operations to avoid conflicts
    localStorage.removeItem('unskippedModules');
    localStorage.removeItem('unskipTimestamp');
    
    // Set flags to force refresh on page load
    localStorage.setItem('forceRefreshSkippedModules', 'true');
    localStorage.setItem('forcedSkip', 'true');
    localStorage.setItem('skipTimestamp', Date.now().toString());
    
    // Force a fresh fetch first to make sure we have latest data
    await skippedModulesStore.fetchSkippedModules(true);
    
    // Update skipped modules in the API (will merge with existing skipped modules)
    const updatedSkippedRoutes = await skippedModulesStore.updateSkippedModules(routesToSkip);
        
    // Verify skip was successful by checking if all routesToSkip are in the updated skipped routes
    const skipSuccessful = routesToSkip.every(route => 
      Array.isArray(updatedSkippedRoutes) && updatedSkippedRoutes.includes(route)
    );
    
    if (!skipSuccessful) {      
      // Force update the local state if API didn't respond correctly
      const existingRoutes = [...skippedModulesStore.skippedRoutes];
      routesToSkip.forEach(route => {
        if (!existingRoutes.includes(route)) {
          existingRoutes.push(route);
        }
      });
      
      skippedModulesStore.skippedRoutes = existingRoutes;
      
      localStorage.setItem('skipped_modules', JSON.stringify({
        skippedRoutes: existingRoutes,
        timestamp: Date.now()
      }));
    }
    
            // Determine which module to redirect to - always go to module 10
    const targetModule = '/steps/total-fundraising-goal/summary';
    
    // Clear the pending redirect since we'll use Vue Router
    localStorage.removeItem('pendingRedirect');
    
    // Dispatch an event so other components can react
    window.dispatchEvent(new CustomEvent('skipped-modules-updated', {
      detail: {
        skippedRoutes: skippedModulesStore.skippedRoutes,
        operation: 'skip',
        timestamp: Date.now()
      }
    }));
    
    // Force a final sync of local state before navigation
    localStorage.setItem('skipped_modules', JSON.stringify({
      skippedRoutes: skippedModulesStore.skippedRoutes,
      timestamp: Date.now()
    }));
    
    // Add delay before redirect to ensure state has propagated
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Use Vue Router navigation instead of window.location to avoid page refresh
    router.push(targetModule);
  } catch (error) {
    console.error('Error skipping modules:', error);
    // Fallback redirect in case of error
    router.push('/steps/total-fundraising-goal/summary');
  }
};

const isUnskipLoading = ref(false);

async function handleUnskip() {
  // Get the current module path
  const currentPathParts = route.path.split('/');
  let currentModulePath = '';
  
  if (currentPathParts.length >= 3) {
    currentModulePath = `/${currentPathParts[1]}/${currentPathParts[2]}`;
  } else {
    console.error('Unable to unskip module: Invalid module path');
    return;
  }
  
  // Set loading state
  isUnskipLoading.value = true;
  
  try {
    // Clean up any previous skip operations to avoid conflicts
    localStorage.removeItem('forcedSkip');
    localStorage.removeItem('skipTimestamp');
    localStorage.removeItem('pendingRedirect');
    
    // Force refresh skipped modules first to ensure we have latest data
    await skippedModulesStore.fetchSkippedModules(true);
    
    // Store which specific module is being unskipped for other components to check
    let unskippedModules = [];
    
    // Determine which modules will be unskipped
    unskippedModules.push(currentModulePath);
    

    
    // Set localStorage values for unskip operation
    localStorage.setItem('unskippedModules', JSON.stringify(unskippedModules));
    localStorage.setItem('unskipTimestamp', Date.now().toString());
    localStorage.setItem('forceRefreshSkippedModules', 'true');
    localStorage.setItem('unskipOperation', 'true');
    
    // Filter out the modules to unskip from the current skipped modules
    const filteredSkippedRoutes = skippedModulesStore.skippedRoutes.filter(
      route => !unskippedModules.includes(route)
    );
    
    // Update the skipped modules in the API
    const updatedSkippedRoutes = await skippedModulesStore.updateSkippedModules(filteredSkippedRoutes);
        
    // Verify the modules were actually unskipped
    const stillSkipped = unskippedModules.some(path => 
      skippedModulesStore.skippedRoutes.includes(path)
    );
    
    if (stillSkipped) {
      console.warn('Modules still appear skipped after unskip operation, forcing local update');
      
      // Apply a more aggressive override by forcing modules to be removed
      const forcedUpdatedRoutes = skippedModulesStore.skippedRoutes.filter(
        route => !unskippedModules.includes(route)
      );
      
      skippedModulesStore.skippedRoutes = forcedUpdatedRoutes;
      
      // Force persist to localStorage
      localStorage.setItem('skipped_modules', JSON.stringify({
        skippedRoutes: forcedUpdatedRoutes,
        timestamp: Date.now()
      }));
    }
    
    // Dispatch an event so other components can react
    window.dispatchEvent(new CustomEvent('module-unskipped', {
      detail: {
        unskippedModules,
        timestamp: Date.now()
      }
    }));
    
    // Add delay before refreshing the view
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Use Vue Router to refresh the current view without a full page reload
    router.replace({
      path: route.path,
      query: { _t: Date.now() } // Add timestamp to force view refresh
    });
  } catch (error) {
    console.error('Error unskipping module:', error);
  } finally {
    isUnskipLoading.value = false;
  }
}
</script>

<style scoped></style>
